import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'

import { ThemeButton, useModalContext } from '../'
import { dataToXLS, getFormattedTime } from '../../core/utils'
import { ExportSomethingDialog } from './export-something.dialog'

const DEFAULT_GET_ALL_DATA = () => new Promise((resolve) => resolve([]))

/**
 * @typedef {object} ExportColumn
 * @property {string} field - Identificador del campo a exportar.
 * @property {string} [headerName] - Nombre del encabezado en el archivo Excel.
 * @property {string} [comboId] - ID del combo para hacer lookup de valores.
 * @property {function} [valueGetter] - Función personalizada para obtener el valor a exportar.
 */

/**
 * @typedef {object} ExportToXLSBtnProps
 * @property {boolean} [exportSelectionEnabled=true] - Si es true, muestra el botón de exportar selección.
 * @property {boolean} [exportAllEnabled=true] - Si es true, muestra el botón de exportar todo.
 * @property {object[]} [selectedRows=[]] - Array de filas seleccionadas para exportar.
 * @property {ExportColumn[]} [columnsExport=[]] - Configuración de columnas a incluir en la exportación.
 * @property {function(): Promise<object[]>} [getAllData] - Función asíncrona para obtener todos los datos cuando se exporta todo.
 * @property {string} [titleRoot='export_'] - Prefijo del nombre del archivo Excel generado.
 * @property {string} [headerNamespace='applications'] - Namespace para las traducciones de encabezados de columnas.
 */

/**
 * Un componente de botones para exportar datos a Excel (XLS/XLSX).
 *
 * Proporciona dos opciones de exportación:
 * - Exportar selección: Exporta solo las filas seleccionadas
 * - Exportar todo: Obtiene y exporta todos los datos disponibles
 *
 * Incluye:
 * - Generación automática de nombres de archivo con timestamp
 * - Conversión de valores usando combos (lookups)
 * - Internacionalización de encabezados
 * - Estados de carga durante la exportación
 * - Diálogo de confirmación cuando no hay selección
 *
 * @component
 * @example
 * const MyDataTable = () => {
 *   const [selectedRows, setSelectedRows] = useState([]);
 *
 *   const columnsExport = [
 *     { field: 'id', headerName: 'ID' },
 *     { field: 'nombre', headerName: 'Nombre' },
 *     { field: 'estado', comboId: 'estados' }
 *   ];
 *
 *   const getAllData = async () => {
 *     const response = await fetch('/api/data');
 *     return response.json();
 *   };
 *
 *   return (
 *     <>
 *       <DataTable
 *         data={data}
 *         selectedRows={selectedRows}
 *         onSelectionChange={setSelectedRows}
 *       />
 *       <ExportToXLSBtn
 *         selectedRows={selectedRows}
 *         columnsExport={columnsExport}
 *         getAllData={getAllData}
 *         titleRoot="usuarios_"
 *         headerNamespace="users"
 *         exportSelectionEnabled={true}
 *         exportAllEnabled={true}
 *       />
 *     </>
 *   );
 * };
 *
 * @param {ExportToXLSBtnProps} props - Las props del componente ExportToXLSBtn.
 */
export const ExportToXLSBtn = ({
  exportSelectionEnabled = true,
  exportAllEnabled = true,
  selectedRows = [],
  columnsExport = [],
  getAllData = DEFAULT_GET_ALL_DATA,
  titleRoot = 'export_',
  headerNamespace = 'applications',
}) => {
  const intl = useIntl()
  const combos = useSelector((state) => state.combos)
  const [loading, setLoading] = useState(false)

  const {
    actions: { open },
  } = useModalContext()

  const mainExport = async (data) => {
    const fileName = `${titleRoot}${getFormattedTime()}.xlsx`
    await dataToXLS(data, columnsExport, combos, intl, headerNamespace, fileName)
  }

  const handleExport = async () => {
    if (!selectedRows?.length) {
      open({ Component: ExportSomethingDialog })
      return
    }

    setLoading(true)
    try {
      await mainExport(selectedRows)
    } catch (error) {
      console.error('Error al exportar la selección:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleExportAll = async () => {
    setLoading(true)
    try {
      const recievedData = await getAllData()
      await mainExport(recievedData)
    } catch (error) {
      console.error('Error al exportar todos los datos:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {exportSelectionEnabled ? (
        <ThemeButton
          color="primary"
          labelM="sm"
          onClick={handleExport}
          style={{ marginRight: '1em' }}
          loading={loading}
          disabled={loading}
        >
          {intl.formatMessage({ id: 'global.exportSelected' })} ({selectedRows.length})
        </ThemeButton>
      ) : null}
      {exportAllEnabled ? (
        <ThemeButton
          color="primary"
          labelM="sm"
          loading={loading}
          onClick={handleExportAll}
          disabled={loading}
        >
          {intl.formatMessage({ id: 'global.exportAll' })}
        </ThemeButton>
      ) : null}
    </>
  )
}
